import { httpClient } from './api-client';
import type { PlatformAuthScope } from '@/models/auth';

interface AuthScopeResponse {
  scopes: Array<{
    scopeKey: string;
    appKind: string;
    role: string;
    tenantId?: string;
    tenantName?: string;
    label: string;
    isDefault: boolean;
  }>;
  suggestedScopeKey?: string;
}

function normalizeScopeResponse(response: AuthScopeResponse | Record<string, unknown> | null | undefined): {
  scopes: AuthScopeResponse['scopes'];
  suggestedScopeKey?: string;
} {
  const rawScopes = Array.isArray(response?.scopes)
    ? response.scopes
    : Array.isArray((response as { Scopes?: unknown } | null | undefined)?.Scopes)
      ? ((response as { Scopes: AuthScopeResponse['scopes'] }).Scopes)
      : [];

  const suggestedScopeKey =
    typeof response?.suggestedScopeKey === 'string'
      ? response.suggestedScopeKey
      : typeof (response as { SuggestedScopeKey?: unknown } | null | undefined)?.SuggestedScopeKey === 'string'
        ? (response as { SuggestedScopeKey: string }).SuggestedScopeKey
        : undefined;

  return {
    scopes: rawScopes,
    suggestedScopeKey,
  };
}

export async function getAdminScopes(): Promise<{
  scopes: PlatformAuthScope[];
  suggestedScopeKey?: string;
}> {
  const response = await httpClient.get<AuthScopeResponse | Record<string, unknown>>('/api/v1/auth/scopes');
  const normalized = normalizeScopeResponse(response);

  const scopes = normalized.scopes
    .filter(
      (scope): scope is AuthScopeResponse['scopes'][number] & { role: 'platform_admin' | 'org_admin'; appKind: 'admin' } =>
        scope.appKind === 'admin' && (scope.role === 'platform_admin' || scope.role === 'org_admin'),
    )
    .map((scope) => ({
      scopeKey: scope.scopeKey,
      appKind: 'admin' as const,
      role: scope.role,
      tenantId: scope.tenantId,
      tenantName: scope.tenantName,
      label: scope.label,
      isDefault: scope.isDefault,
    }));

  return {
    scopes,
    suggestedScopeKey: normalized.suggestedScopeKey,
  };
}
